import React, { useEffect } from 'react';
import ClinicCard from '../components/ClinicCard';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import { formatCityDisplay } from '../utils/helpers';

const StaticListPage = ({ navigateToHome, navigateToBlog, navigateToB2B, staticPageClinics, staticPageInfo, onViewClinic, navigateToPrivacy, navigateToTerms, navigateToAbout, navigateToSitemap }) => {
  // Build SEO title & description
  const city = staticPageInfo.city || '';
  const district = staticPageInfo.district || '';
  const location = district ? `${city} ${district}` : city;

  const seoTitle = district 
    ? `Fogorvos, Fogászat - ${city} - ${district} | FogPortál.hu`
    : `Fogorvos, Fogászat - ${city} | FogPortál.hu`;

  const seoDescription = district
    ? `Keresse meg a legjobb fogorvosokat ${city} területén! Listánkban megbízható szakemberek és rendelők szerepelnek ${city} ${district} részén. Foglaljon időpontot!`
    : `Keresse meg a legjobb fogorvosokat ${city} területén! Listánkban megbízható fogászati szakemberek és rendelők szerepelnek. Foglaljon időpontot!`;

  const seoKeywords = `fogorvos ${location}, fogászat ${location}, fogász ${city}, fogászati rendelő ${location}`;
  const canonicalPath = window.location.pathname;

  // Format display names
  const getDisplayCity = () => {
    if (!staticPageInfo.city) return '';
    return staticPageInfo.city.charAt(0).toUpperCase() + staticPageInfo.city.slice(1);
  };

  const getDisplayDistrict = () => {
    if (!staticPageInfo.district) return '';
    return staticPageInfo.district;
  };

  const getPageTitle = () => {
    let title = "Fogorvos " + getDisplayCity();
    if (staticPageInfo.district) {
      // Convert district format for display
      title += " " + staticPageInfo.district.replace(/(\d+)\./, '$1.');
    }
    return title;
  };

  const getPageDescription = () => {
    let desc = `${getDisplayCity()}`;
    if (staticPageInfo.district) {
      desc += ` ${staticPageInfo.district}`;
    }
    desc += " fogorvosai és fogászatai";
    return desc;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={`${window.location.origin}${canonicalPath}`}
      />
      {/* Modern Header */}
      <header className="bg-white shadow-lg border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button 
                onClick={navigateToHome}
                className="flex items-center space-x-3"
              >
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 2.21 1.79 4 4 4s4-1.79 4-4v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2 15c0 1.1-.9 2-2 2s-2-.9-2-2v-1h4v1zm1.46-4.86C14.8 12.64 14 13.26 14 14v1h-4v-1c0-.74-.8-1.36-1.46-.86C7.06 12.5 6 10.84 6 9c0-3.31 2.69-6 6-6s6 2.69 6 6c0 1.84-1.06 3.5-2.54 4.14z"/>
                  </svg>
                </div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                  FogPortál.hu
                </h1>
              </button>
            </div>

            <nav className="hidden md:flex items-center space-x-1">
              <button 
                onClick={navigateToHome}
                className="px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium text-sm transition-all duration-200"
              >
                Főoldal
              </button>
              {/* TEMPORARILY HIDDEN - Blog Menu Item */}
              {/* <button 
                onClick={navigateToBlog}
                className="px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium text-sm transition-all duration-200"
              >
                Blog
              </button> */}
              <button 
                onClick={navigateToB2B}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-lg font-medium text-sm hover:from-blue-700 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg ml-2"
              >
                Megjelenés fogászatoknak
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white p-8">
            <h1 className="text-4xl font-bold">{getPageTitle()}</h1>
            <p className="text-xl mt-2 opacity-90">{getPageDescription()}</p>
          </div>

          <div className="p-8">
            {staticPageClinics.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                  </svg>
                </div>
                <p className="text-xl text-gray-600 mb-4">Még nincsenek regisztrált fogorvosok ezen a területen</p>
                <p className="text-gray-500">Kérjük próbálja újra később, vagy keressen más városokban.</p>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <p className="text-gray-600 text-lg">
                    <span className="font-semibold text-blue-600">{staticPageClinics.length} fogászat</span> található 
                    {staticPageInfo.district ? ` ${getDisplayCity()} ${getDisplayDistrict()} területén` : ` ${getDisplayCity()} városában`}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {staticPageClinics.map((clinic) => (
                    <ClinicCard key={clinic.id} clinic={clinic} showGoogleReviews={true} onViewClinic={onViewClinic} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer 
        navigateToHome={navigateToHome}
        navigateToBlog={navigateToBlog}
        navigateToB2B={navigateToB2B}
        navigateToPrivacy={() => window.location.href = '/adatvedelem'}
        navigateToTerms={() => window.location.href = '/aszf'}
        navigateToAbout={() => window.location.href = '/rolunk'}
        navigateToSitemap={navigateToSitemap}
      />
    </div>
  );
};


export default StaticListPage;
